import { sanitize } from "../utils/sanitizer";

export enum ErrorType {
	VOICE_INPUT = "voice_input",
	AUDIO_RECORDING = "audio_recording",
	COMMENT_GENERATION = "comment_generation",
	STORAGE = "storage",
	SESSION = "session",
	TRANSCRIPTION = "transcription",
	NETWORK = "network",
	PERMISSION = "permission",
	BROWSER_COMPATIBILITY = "browser_compatibility",
	CONFIG = "config",
}

export enum ErrorSeverity {
	LOW = "low",
	MEDIUM = "medium",
	HIGH = "high",
	CRITICAL = "critical",
}

export interface MonologError {
	type: ErrorType;
	severity: ErrorSeverity;
	message: string;
	originalError?: Error;
	timestamp: Date;
	context?: Record<string, unknown> | string;
	recoveryAction?: string;
}

export interface ErrorRecoveryStrategy {
	canRecover: boolean;
	fallbackAction?: () => Promise<void>;
	userMessage: string;
	retryable: boolean;
	actionLabel?: string;
}

export class ErrorHandler {
	private static instance: ErrorHandler;
	private errorLog: MonologError[] = [];
	private maxLogSize = 100;
	private isHandlingError = false; // Reentrancy guard
	private errorCallbacks: Map<ErrorType, ((error: MonologError) => void)[]> =
		new Map();

	private constructor() {}

	static getInstance(): ErrorHandler {
		if (!ErrorHandler.instance) {
			ErrorHandler.instance = new ErrorHandler();
		}
		return ErrorHandler.instance;
	}

	// Register error callback for specific error types
	onError(type: ErrorType, callback: (error: MonologError) => void): void {
		if (!this.errorCallbacks.has(type)) {
			this.errorCallbacks.set(type, []);
		}
		this.errorCallbacks.get(type)?.push(callback);
	}

	// Handle error with recovery strategy
	async handleError(error: MonologError): Promise<ErrorRecoveryStrategy> {
		// Prevent infinite recursion
		if (this.isHandlingError) {
			console.warn(
				"Recursive error detected, skipping handling:",
				error.message,
			);
			return {
				canRecover: false,
				userMessage: "エラー復旧中にエラーが発生しました。",
				retryable: false,
			};
		}

		this.isHandlingError = true;

		try {
			// Get sanitized version of the error for both logging and callbacks
			const sanitized = this.sanitizeMonologError(error);

			// Log the error (passing sanitized to avoid redundant work and ensure consistency)
			this.logError(sanitized);

			// Notify callbacks with the sanitized error
			const callbacks = this.errorCallbacks.get(sanitized.type) || [];
			callbacks.forEach((callback) => {
				try {
					callback(sanitized);
				} catch (callbackError) {
					console.error("Error in error callback:", callbackError);
				}
			});

			// Determine recovery strategy
			const strategy = this.getRecoveryStrategy(sanitized);

			// Execute fallback if available
			if (strategy.canRecover && strategy.fallbackAction) {
				try {
					await strategy.fallbackAction();
				} catch (fallbackError) {
					console.error("Fallback action failed:", fallbackError);
					return {
						canRecover: false,
						userMessage:
							"システムの復旧に失敗しました。ページを更新してください。",
						retryable: false,
					};
				}
			}

			return strategy;
		} finally {
			this.isHandlingError = false;
		}
	}

	private logError(error: MonologError): void {
		this.errorLog.push(error);

		// Keep log size manageable
		if (this.errorLog.length > this.maxLogSize) {
			this.errorLog.shift();
		}

		// Console logging based on severity
		const logMessage = `[${error.type}] ${error.message}`;
		const context = error.context
			? ` | Context: ${JSON.stringify(error.context)}`
			: "";

		switch (error.severity) {
			case ErrorSeverity.CRITICAL:
				console.error(logMessage + context, error.originalError);
				break;
			case ErrorSeverity.HIGH:
				console.error(logMessage + context, error.originalError);
				break;
			case ErrorSeverity.MEDIUM:
				console.warn(logMessage + context, error.originalError);
				break;
			case ErrorSeverity.LOW:
				console.info(logMessage + context, error.originalError);
				break;
		}
	}

	/**
	 * Fully sanitizes a MonologError object before storage or logging
	 */
	private sanitizeMonologError(error: MonologError): MonologError {
		let sanitizedContext: Record<string, unknown> | string | undefined;

		if (typeof error.context === "string") {
			sanitizedContext = sanitize(error.context);
		} else if (error.context && typeof error.context === "object") {
			sanitizedContext = this.sanitizeObject(
				error.context as Record<string, unknown>,
			);
		}

		return {
			...error,
			message: sanitize(error.message),
			context: sanitizedContext,
			originalError: this.sanitizeErrorObject(error.originalError),
		};
	}

	/**
	 * Masks sensitive fields in a plain object, with circular reference protection.
	 */
	private sanitizeObject(
		obj: Record<string, unknown>,
		seen: WeakSet<object> = new WeakSet(),
	): Record<string, unknown> | string {
		if (seen.has(obj)) {
			return "[CIRCULAR]";
		}
		seen.add(obj);

		const sensitiveKeys = [
			"apiKey",
			"token",
			"password",
			"secret",
			"transcript",
			"text",
		];
		const sanitized: Record<string, unknown> = {};

		for (const [key, value] of Object.entries(obj)) {
			if (
				sensitiveKeys.some((k) => key.toLowerCase().includes(k.toLowerCase()))
			) {
				sanitized[key] = "[MASKED]";
			} else if (typeof value === "string") {
				sanitized[key] = sanitize(value);
			} else if (value && typeof value === "object") {
				if (Array.isArray(value)) {
					sanitized[key] = value.map((item) => {
						if (item && typeof item === "object") {
							return this.sanitizeObject(item as Record<string, unknown>, seen);
						}
						return typeof item === "string" ? sanitize(item) : item;
					});
				} else {
					sanitized[key] = this.sanitizeObject(
						value as Record<string, unknown>,
						seen,
					);
				}
			} else {
				sanitized[key] = value;
			}
		}

		return sanitized;
	}

	/**
	 * Creates a safe-to-log version of an Error object
	 */
	private sanitizeErrorObject(error?: Error): Error | undefined {
		if (!error) return undefined;

		const sanitizedMessage = sanitize(error.message);
		const newError = new Error(sanitizedMessage);

		if (error.stack) {
			newError.stack = sanitize(error.stack);
		} else {
			newError.stack = "[STACK_TRACE_NOT_AVAILABLE]";
		}

		return newError;
	}

	public getRecoveryStrategy(error: MonologError): ErrorRecoveryStrategy {
		switch (error.type) {
			case ErrorType.VOICE_INPUT:
				return this.getVoiceInputRecovery(error);
			case ErrorType.AUDIO_RECORDING:
				return this.getAudioRecordingRecovery(error);
			case ErrorType.COMMENT_GENERATION:
				return this.getCommentGenerationRecovery(error);
			case ErrorType.STORAGE:
				return this.getStorageRecovery(error);
			case ErrorType.SESSION:
				return this.getSessionRecovery(error);
			case ErrorType.TRANSCRIPTION:
				return this.getTranscriptionRecovery(error);
			case ErrorType.NETWORK:
				return this.getNetworkRecovery(error);
			case ErrorType.PERMISSION:
				return this.getPermissionRecovery(error);
			case ErrorType.BROWSER_COMPATIBILITY:
				return this.getBrowserCompatibilityRecovery(error);
			case ErrorType.CONFIG:
				return this.getConfigRecovery(error);
			default:
				return {
					canRecover: false,
					userMessage:
						"予期しないエラーが発生しました。もう一度お試しください。",
					retryable: true,
				};
		}
	}

	private getVoiceInputRecovery(_error: MonologError): ErrorRecoveryStrategy {
		return {
			canRecover: true,
			fallbackAction: async () => {
				// Attempt to restart voice recognition
				console.log("Attempting to restart voice recognition...");
			},
			userMessage: "音声入力が一時的に利用できません。再接続を試みています...",
			retryable: true,
		};
	}

	private getAudioRecordingRecovery(
		_error: MonologError,
	): ErrorRecoveryStrategy {
		return {
			canRecover: true,
			fallbackAction: async () => {
				// Continue session without recording
				console.log("Continuing session without audio recording...");
			},
			userMessage: "録音が無効です。録音なしでセッションを続行します。",
			retryable: false,
		};
	}

	private getCommentGenerationRecovery(
		error: MonologError,
	): ErrorRecoveryStrategy {
		return {
			canRecover: false,
			userMessage:
				error.message ||
				"コメント生成に失敗しました。APIキーの設定やネットワーク接続を確認してください。",
			retryable: true,
		};
	}

	private getStorageRecovery(_error: MonologError): ErrorRecoveryStrategy {
		return {
			canRecover: true,
			fallbackAction: async () => {
				// Use in-memory storage
				console.log("Using temporary in-memory storage...");
			},
			userMessage:
				"ストレージが一時的に利用できません。セッションデータは保存されません。",
			retryable: true,
		};
	}

	private getSessionRecovery(_error: MonologError): ErrorRecoveryStrategy {
		return {
			canRecover: true,
			fallbackAction: async () => {
				// Create new session
				console.log("Creating new session...");
			},
			userMessage:
				"セッションエラーが発生しました。新しいセッションを開始します。",
			retryable: false,
		};
	}

	private getTranscriptionRecovery(
		_error: MonologError,
	): ErrorRecoveryStrategy {
		return {
			canRecover: true,
			fallbackAction: async () => {
				// Attempt to restart or notify
				console.log("Transcription error occurred");
			},
			userMessage:
				"文字起こしサービスでエラーが発生しました。接続を確認してください。",
			retryable: true,
		};
	}

	private getNetworkRecovery(error: MonologError): ErrorRecoveryStrategy {
		return {
			canRecover: true,
			fallbackAction: async () => {
				// Enable offline mode
				console.log("Enabling offline mode...");
			},
			userMessage:
				error.message ||
				"ネットワークが利用できません。制限された機能でオフラインモードを実行しています。",
			retryable: true,
		};
	}

	private getPermissionRecovery(_error: MonologError): ErrorRecoveryStrategy {
		return {
			canRecover: false,
			userMessage:
				"マイクの許可が必要です。マイクへのアクセスを有効にしてからページを更新してください。",
			retryable: false,
		};
	}

	private getBrowserCompatibilityRecovery(
		error: MonologError,
	): ErrorRecoveryStrategy {
		let message =
			"お使いのブラウザは必要な機能をサポートしていません。最新のブラウザを使用してください。";

		if (
			error.message.toLowerCase().includes("voice") ||
			error.message.toLowerCase().includes("speech")
		) {
			message =
				"このブラウザでは音声入力（Web Speech API）がサポートされていません。フル機能を利用するには、ChromeまたはEdgeを使用してください。";
		}

		return {
			canRecover: false,
			userMessage: message,
			retryable: false,
		};
	}

	private getConfigRecovery(error: MonologError): ErrorRecoveryStrategy {
		return {
			canRecover: true,
			userMessage: error.message,
			retryable: true,
			actionLabel: "設定を開く",
		};
	}

	// Get error statistics
	getErrorStats(): { type: ErrorType; count: number; lastOccurrence: Date }[] {
		const stats = new Map<ErrorType, { count: number; lastOccurrence: Date }>();

		this.errorLog.forEach((error) => {
			const existing = stats.get(error.type);
			if (existing) {
				existing.count++;
				if (error.timestamp > existing.lastOccurrence) {
					existing.lastOccurrence = error.timestamp;
				}
			} else {
				stats.set(error.type, { count: 1, lastOccurrence: error.timestamp });
			}
		});

		return Array.from(stats.entries()).map(([type, data]) => ({
			type,
			count: data.count,
			lastOccurrence: data.lastOccurrence,
		}));
	}

	// Clear error log and reset reentrancy guard
	clearErrorLog(): void {
		this.errorLog = [];
		this.isHandlingError = false;
	}

	// Reset for testing
	reset(): void {
		this.clearErrorLog();
		this.isHandlingError = false;
	}

	// Get recent errors
	getRecentErrors(limit: number = 10): MonologError[] {
		return this.errorLog.slice(-limit);
	}
}

// Utility function to create standardized errors
export function createError(
	type: ErrorType,
	severity: ErrorSeverity,
	message: string,
	originalError?: Error,
	context?: Record<string, unknown> | string,
	recoveryAction?: string,
): MonologError {
	return {
		type,
		severity,
		message,
		originalError,
		timestamp: new Date(),
		context,
		recoveryAction,
	};
}

/**
 * Custom error class that implements MonologError interface.
 * Useful for throwing typed errors from anywhere in the application.
 */
export class MonologAppError extends Error implements MonologError {
	public timestamp: Date;

	constructor(
		public type: ErrorType,
		public severity: ErrorSeverity,
		message: string,
		public originalError?: Error,
		public context?: Record<string, unknown> | string,
		public recoveryAction?: string,
	) {
		super(message);
		this.name = "MonologAppError";
		this.timestamp = new Date();

		// Set the prototype explicitly (important for custom errors in TS)
		Object.setPrototypeOf(this, MonologAppError.prototype);
	}
}

// Global error handler instance
export const errorHandler = ErrorHandler.getInstance();
